import { Switch } from 'antd';
import { get } from 'lodash-es';
import { memo, useState } from 'react';

import SettingIcon from '@asset/settings.icon.svg?react';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { ReadMore } from '@atom/read-more/read-more';
import { ServiceIcon } from '@atom/service-icon/service-icon';
import { Text } from '@atom/typography/typography';
import { ColorEnum } from '@enum/style/color.enum';
import { UnifiedApiEnum } from '@enum/unified-api.enum';
import { useMedia } from '@hook/style/use-media.hook';
import { ConnectorInterface } from '@interface/connector/connector.interface';
import { isString } from '@util/data/is-exist';

import { useConnectorCard } from './connector-card.hook';
import {
  Card,
  CardFooter,
  CardTitle,
  ColCard,
  ConfigurationButton,
  ConfigurationWrapper,
  ContentWrapper,
  Divider,
  HeaderWrapper,
  Tag,
} from './connector-card.style';

interface ConnectorCardProps {
  connector: ConnectorInterface;
  shouldConfigure?: boolean;
}

const UnifiedApiEnumList = Object.entries(UnifiedApiEnum);

export const ConnectorCard = memo(({ connector, shouldConfigure = true }: ConnectorCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { integration, isLoading, onChange, path } = useConnectorCard(connector);
  const { isMobile } = useMedia();

  const handleExpand = (e: React.MouseEvent<HTMLElement>, info: { expanded: boolean }) => setIsExpanded(info.expanded);

  return (
    <ColCard isMobile={isMobile} span={8}>
      <Card
        bordered={false}
        title={
          <HeaderWrapper>
            <ServiceIcon height={40} icon={connector.serviceId} width={40} />
            {shouldConfigure && (
              <ConfigurationWrapper>
                <ConfigurationButton
                  href={path}
                  icon={<SettingIcon fill={ColorEnum.Teal100} width={24} />}
                  type={ButtonTypeEnum.Link}
                >
                  <Text color={ColorEnum.Inherit} level='m'>
                    Configure
                  </Text>
                </ConfigurationButton>
              </ConfigurationWrapper>
            )}
          </HeaderWrapper>
        }
      >
        <ContentWrapper>
          <Tag>
            {get(
              UnifiedApiEnumList.find(([, key]) => key === connector.unifiedApi),
              '0',
            )}
          </Tag>
          <CardTitle
            title={connector.name}
            description={
              <Text
                color={ColorEnum.Black70}
                mb={8}
                ellipsis={{
                  rows: 3,
                  expanded: isExpanded,
                  expandable: 'collapsible',
                  onExpand: handleExpand,
                  symbol: (expanded) => (expanded ? 'Show less' : 'Read more'),
                }}
                paragraph
              >
                {connector.description}
              </Text>
            }
          />
          {isString(connector.docUrl) && <ReadMore target='_blank' title='Read docs' to={connector.docUrl} />}
        </ContentWrapper>
        <Divider />
        <CardFooter>
          <Switch checked={integration?.isEnabled} loading={isLoading} onChange={onChange} />
        </CardFooter>
      </Card>
    </ColCard>
  );
});
